import { useEffect, useState } from "react";

import { useSelector } from "react-redux";

import { ServiceConstants } from "constant";
import { ContractDocumentService } from "services";
import { Bank, ContractDocumentOptionsResponse, ContractDocumentResponse, ContractDocumentTypeOption } from "types";

const {
  REQUEST_CONTRACT_DOCUMENT_LIST_ID,
  REQUEST_CONTRACT_DOCUMENT_OPTIONS_ID,
  REQUEST_CONTRACT_DOCUMENT_CREATE_ID,
  REQUEST_CONTRACT_DOCUMENT_UPDATE_ID,
  REQUEST_CONTRACT_DOCUMENT_DELETE_ID,
  REQUEST_PENDING,
  REQUEST_SUCCESS,
} = ServiceConstants;

const BANK_NOW_FIXED_DOCUMENT_TYPES = [
  "ONLINE_IDENTIFICATION",
  "COPY_BANK_CARD",
  "DEBT_COLLECTION_INFO",
];

const getFirstFixedDocumentType = (bank: Bank, clientVisaType?: "B" | "C") => {
  if (bank === "CEMBRA_MONEY_AG" || bank === "CORNER") {
    if (clientVisaType === "B") {
      return "VERIFIED_RESIDENCE_PERMIT_B";
    }

    if (clientVisaType === "C") {
      return "VERIFIED_RESIDENCE_PERMIT_C";
    }

    return "VERIFIED_ID_CARD";
  }

  return BANK_NOW_FIXED_DOCUMENT_TYPES[0];
};

export const useContractDocuments = (
  bank: Bank,
  contractId: number | undefined,
  clientVisaType?: "B" | "C",
) => {
  const fixedDocumentTypes = [
    getFirstFixedDocumentType(bank, clientVisaType),
    "COPY_BANK_CARD",
    "DEBT_COLLECTION_INFO",
  ];
  const [documentTypeOptions, setDocumentTypeOptions] = useState<ContractDocumentTypeOption[]>([]);
  const [contractDocuments, setContractDocuments] = useState<ContractDocumentResponse[]>([]);
  const [isAddingContractDocument, setIsAddingContractDocument] = useState(false);
  const [newContractDocumentType, setNewContractDocumentType] = useState<string | null>(null);

  const documentOptionsRequestKey = REQUEST_CONTRACT_DOCUMENT_OPTIONS_ID + bank;
  const contractDocumentsRequestKey = contractId
    ? REQUEST_CONTRACT_DOCUMENT_LIST_ID + contractId
    : undefined;

  const contractDocumentOptionsResponse = useSelector(
    (state: any) => state.service.object[documentOptionsRequestKey]
  );
  const contractDocumentsResponse = useSelector(
    (state: any) =>
      contractDocumentsRequestKey ? state.service.object[contractDocumentsRequestKey] : undefined
  );
  const contractDocumentCreateResponse = useSelector(
    (state: any) => state.service.object[REQUEST_CONTRACT_DOCUMENT_CREATE_ID]
  );
  const contractDocumentUpdateResponse = useSelector(
    (state: any) => state.service.object[REQUEST_CONTRACT_DOCUMENT_UPDATE_ID]
  );
  const contractDocumentDeleteResponse = useSelector(
    (state: any) => state.service.object[REQUEST_CONTRACT_DOCUMENT_DELETE_ID]
  );

  // Reset on bank change
  useEffect(() => {
    setDocumentTypeOptions([]);
    setContractDocuments([]);
    setIsAddingContractDocument(false);
    setNewContractDocumentType(null);
  }, [bank]);

  // Fetch document type options for the bank
  useEffect(() => {
    if (bank) {
      ContractDocumentService.getContractDocumentOptions(bank);
    }
  }, [bank]);

  // Fetch documents when contractId becomes available
  useEffect(() => {
    if (contractId) {
      ContractDocumentService.getContractDocuments(contractId);
    }
  }, [contractId]);

  // Handle document type options response
  useEffect(() => {
    const optionsData = contractDocumentOptionsResponse?.data as ContractDocumentOptionsResponse | undefined;
    if (optionsData?.contractDocumentTypes) {
      setDocumentTypeOptions(
        Array.isArray(optionsData.contractDocumentTypes) ? optionsData.contractDocumentTypes : []
      );
    }
  }, [bank, contractDocumentOptionsResponse]);

  // Handle documents list response
  useEffect(() => {
    if (contractDocumentsResponse?.data && Array.isArray(contractDocumentsResponse.data)) {
      setContractDocuments(contractDocumentsResponse.data as ContractDocumentResponse[]);
    }
  }, [contractDocumentsResponse]);

  // Handle create/update/delete responses
  useEffect(() => {
    if (!contractId) return;

    const anySuccess =
      contractDocumentCreateResponse?.status === REQUEST_SUCCESS ||
      contractDocumentUpdateResponse?.status === REQUEST_SUCCESS ||
      contractDocumentDeleteResponse?.status === REQUEST_SUCCESS;

    if (anySuccess) {
      if (contractDocumentCreateResponse?.status === REQUEST_SUCCESS) {
        setIsAddingContractDocument(false);
        setNewContractDocumentType(null);
      }
      ContractDocumentService.getContractDocuments(contractId);
    }
  }, [
    contractDocumentCreateResponse,
    contractDocumentUpdateResponse,
    contractDocumentDeleteResponse,
    contractId,
  ]);

  const fixedDocuments = fixedDocumentTypes.map((type) => ({
    type,
    document: contractDocuments.find((doc) => doc.type === type) ?? null,
  }));

  const dynamicDocuments = contractDocuments.filter(
    (doc) => !fixedDocumentTypes.includes(doc.type)
  );

  const availableDocumentTypeOptions = documentTypeOptions.filter(
    (option) =>
      !fixedDocumentTypes.includes(option.value) &&
      !contractDocuments.some((doc) => doc.type === option.value)
  );

  const isDocumentChecked = (documentType: string) =>
    contractDocuments.some((doc) => doc.type === documentType && doc.checked);

  const getDocumentTypeLabel = (documentType: string) =>
    documentTypeOptions.find((option) => option.value === documentType)?.label ?? documentType;

  const handleContractDocumentChange = (documentType: string, checked: boolean) => {
    if (!contractId) return;

    const existing = contractDocuments.find((doc) => doc.type === documentType);
    if (existing) {
      ContractDocumentService.updateContractDocument(existing.id, { type: documentType, checked });
      return;
    }

    if (checked) {
      ContractDocumentService.createContractDocument({ contractId, type: documentType, checked });
    }
  };

  const handleCreateContractDocument = () => {
    if (!contractId || !newContractDocumentType) return;

    const alreadyExists = contractDocuments.some((doc) => doc.type === newContractDocumentType);
    if (alreadyExists) {
      setIsAddingContractDocument(false);
      setNewContractDocumentType(null);
      return;
    }

    ContractDocumentService.createContractDocument({
      contractId,
      type: newContractDocumentType,
      checked: false,
    });
  };

  const handleDeleteContractDocument = (documentId: number) => {
    ContractDocumentService.deleteContractDocument(documentId);
  };

  return {
    documentTypeOptions,
    contractDocuments,
    fixedDocuments,
    dynamicDocuments,
    isAddingContractDocument,
    newContractDocumentType,
    availableDocumentTypeOptions,
    isCreatingContractDocument: contractDocumentCreateResponse?.status === REQUEST_PENDING,
    isDeletingContractDocument: contractDocumentDeleteResponse?.status === REQUEST_PENDING,
    setIsAddingContractDocument,
    setNewContractDocumentType,
    isDocumentChecked,
    getDocumentTypeLabel,
    handleContractDocumentChange,
    handleCreateContractDocument,
    handleDeleteContractDocument,
  };
};
